import { useEffect, useRef, useState } from "react";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../../../common/routesUtils";
import GraphMap from "../../../../../../components/charts/GraphMap";
import { MarkerLayer } from "../../../../../../components/charts/GraphMap/layers";
import PaperCard from "../../../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../../../components/widgets/PaperCard/PaperCardHeader";
import { PageType } from "../../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../../context/BreadcrumbsProvider";
import { NodesMapDTO } from "../../../../../../datasources/api-client/data-contracts";
import { ThemeColors } from "../../../../../../theme/themeValues";
import { MapBounds } from "../../../../../results/ResultsOperationalUnits/useOperationalUnitsData";

type NodesMapType = {
	data: NodesMapDTO | undefined;
	isLoading: boolean | undefined;
	reload: VoidFunction | undefined;
};

type Props = {
	map: NodesMapType;
	onBoundsChange: (bounds: MapBounds) => void;
	mapBounds?: MapBounds;
};

export const MapOperationalUnits = ({ map, onBoundsChange, mapBounds }: Props) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation("dataCollection");
	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();

	const [stableNodesData, setStableNodesData] = useState<NodesMapDTO["items"] | undefined>(map.data?.items);

	useEffect(() => {
		if (!map.isLoading && map.data?.items) {
			setStableNodesData(map.data.items);
		}
	}, [map.isLoading, map.data?.items]);

	const handleNavigate = (node: { code?: string | null; id?: string | null }) => {
		if (!node.code || !node.id) return;

		visitPage(PageType.DataCollectionConfigurationOperationalUnitsDetail, false, node.code, node.id);
		navigate(AppRoutes.DataCollectionConfigurationOperationalUnitsDetail.replace(":id", node.id));
	};

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader
					containerRef={containerRef}
					title={t("operational units/map of greenrouter operational units")}
					isLoading={map.isLoading}
					onReload={map.reload}
				/>
			}
			content={
				<Stack sx={{ height: "500px" }}>
					<GraphMap
						tileProvider={"heremaps"}
						isLoading={map.isLoading}
						zoom={3}
						maxZoom={20}
						minZoom={1}
						bounds={mapBounds}
						onBoundsChange={onBoundsChange}
					>
						{stableNodesData && (
							<MarkerLayer
								nodes={stableNodesData}
								color={ThemeColors.LightPrimary}
								onNavigate={handleNavigate}
							/>
						)}
					</GraphMap>
				</Stack>
			}
			sxContent={{ paddingRight: 2, paddingLeft: 2 }}
		/>
	);
};
